import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/api';
import { Heart, MessageCircle, Star, Share2, Trash2, User, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/api';
import BottomNav from '../components/common/BottomNav';
import TopHeader from '../components/common/TopHeader';
import AdminEventsWidget from '../components/AdminEventsWidget';
import HighlightsStrip from '../components/feed/HighlightsStrip';
import MADSection from '../components/feed/MADSection';
import CommentModal from '../components/modals/CommentModal';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import WallModal from '../components/modals/WallModal';
import OrgPollModal from '../components/modals/OrgPollModal';
import FeedPhoto from '../components/shared/FeedPhoto';
import { SuggestIdeaModal, VoteIdeasModal } from '../components/modals/IdeaModals';
import { formatDate, TEAL_ACCENT, TEAL_MUTED, ORANGE_ACCENT, ORANGE_MUTED } from '../utils/homeHelpers';

const HomePage = ({ setShowTour }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [allChallenges, setAllChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [posts, setPosts] = useState([]);
  const [adminEvents, setAdminEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const isHRAdmin = user?.role === 'HR_ADMIN';

  // Feed interactions
  const [likedActions, setLikedActions] = useState(new Set());
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [joinedActions, setJoinedActions] = useState(new Set());

  // Highlights & Wall
  const [highlights, setHighlights] = useState([]);
  const [showWallModal, setShowWallModal] = useState(null);
  const [wallData, setWallData] = useState(null);
  const [wallLoading, setWallLoading] = useState(false);
  const [wallMessage, setWallMessage] = useState('');

  // Poll modal (voting admin_events opened from the Highlights strip)
  const [activePoll, setActivePoll] = useState(null);

  // Comments
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [activePostForComment, setActivePostForComment] = useState(null);
  const [activeItemType, setActiveItemType] = useState('post');
  const [commentText, setCommentText] = useState('');
  // Kept in sync with @-tags actually present in `commentText` by
  // MentionInput; sent to POST /comment as the structured `mentions`
  // array, then cleared after each submit.
  const [commentMentions, setCommentMentions] = useState([]);
  const [postComments, setPostComments] = useState({});
  const [madActionComments, setMadActionComments] = useState({});
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Ideas & Voting
  const [showSuggestIdeaModal, setShowSuggestIdeaModal] = useState(false);
  const [showVoteOnIdeasModal, setShowVoteOnIdeasModal] = useState(false);
  const [selectedAdminEvent, setSelectedAdminEvent] = useState(null);
  const [submittedIdeas, setSubmittedIdeas] = useState([]);
  const [ideaForm, setIdeaForm] = useState({ title: '', description: '', location: '', agenda: '' });
  const [currentIdeaIndex, setCurrentIdeaIndex] = useState(0);

  // === DATA FETCHING ===
  useEffect(() => { fetchData(); }, [location.key, location.state?.refresh, refreshKey]);

  const fetchData = async () => {
    try {
      const [challengesRes, myChallengesRes, postsRes, adminEventsRes, highlightsRes] = await Promise.all([
        axios.get(`${API}/challenges?active_only=true`).catch(() => ({ data: [] })),
        axios.get(`${API}/challenges/my`).catch(() => ({ data: [] })),
        axios.get(`${API}/posts?limit=20`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin-events`).catch(() => ({ data: [] })),
        axios.get(`${API}/highlights`).catch(() => ({ data: [] }))
      ]);
      setAllChallenges(challengesRes.data || []);
      setMyChallenges(myChallengesRes.data || []);
      setPosts(postsRes.data || []);
      setAdminEvents(adminEventsRes.data || []);
      setHighlights(highlightsRes.data || []);

      // Seed likedPosts from server data so the heart icon shows the
      // correct filled/empty state on first render for posts the user
      // has already liked. Previously `likedPosts` started empty and
      // only reflected likes toggled during this session.
      const currentUid = user?.id;
      if (currentUid) {
        const alreadyLiked = new Set();
        (postsRes.data || []).forEach(p => {
          if (Array.isArray(p.likes) && p.likes.includes(currentUid)) {
            alreadyLiked.add(p.id);
          }
        });
        setLikedPosts(alreadyLiked);
      }

      // Seed joinedActions from server data so the carousel "Join" button
      // shows the correct state on first render (previously the Set was
      // only populated when the user joined locally within this session).
      const uid = user?.id;
      const joined = new Set();
      (myChallengesRes.data || []).forEach(c => joined.add(c.id));
      if (uid) {
        (challengesRes.data || []).forEach(c => {
          const parts = c.participants || [];
          if (parts.some(p => (typeof p === 'string' ? p : p?.user_id) === uid)) {
            joined.add(c.id);
          }
        });
      }
      setJoinedActions(joined);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // === HR HANDLERS ===
  const handleHRFlagChallenge = async (challengeId, reason) => {
    try {
      await axios.post(`${API}/hr/challenges/${challengeId}/flag${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
      alert('Action flagged for review');
      setRefreshKey(prev => prev + 1);
    } catch (error) { alert(error.response?.data?.detail || 'Failed to flag MAD Action'); }
  };

  const handleHRDeletePost = async (postId) => {
    try {
      const reason = window.prompt('Reason for removal (optional):');
      await axios.post(`${API}/hr/posts/${postId}/delete${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
      alert('Post removed by HR');
    } catch (error) { alert(error.response?.data?.detail || 'Failed to remove post'); }
  };

  // === DELETE HANDLERS ===
  const handleDeletePost = async (postId) => {
    try { await axios.delete(`${API}/posts/${postId}`); setPosts(prev => prev.filter(p => p.id !== postId)); setDeleteConfirm(null); setRefreshKey(prev => prev + 1); }
    catch (error) { alert(error.response?.data?.detail || 'Failed to delete post'); }
  };

  const handleDeleteChallenge = async (challengeId) => {
    try { await axios.delete(`${API}/challenges/${challengeId}`); setAllChallenges(prev => prev.filter(c => c.id !== challengeId)); setDeleteConfirm(null); setRefreshKey(prev => prev + 1); }
    catch (error) { alert(error.response?.data?.detail || 'Failed to delete MAD Action'); }
  };

  const handleDeleteComment = async (commentId, postId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      setPostComments(prev => ({ ...prev, [postId]: (prev[postId] || []).filter(c => c.id !== commentId) }));
      setMadActionComments(prev => ({ ...prev, [postId]: (prev[postId] || []).filter(c => c.id !== commentId) }));
      // Keep the post-card comment badge in sync with the modal.
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: (p.comments || []).filter(c => c.id !== commentId), comments_count: Math.max(0, (p.comments_count ?? 0) - 1) } : p));
      setDeleteConfirm(null);
    } catch (error) { alert(error.response?.data?.detail || 'Failed to delete comment'); }
  };

  // === WALL HANDLERS ===
  const openWall = async (highlight) => {
    // Poll highlights come from voting admin_events — they don't have a
    // wall behind them, they open the org poll modal instead.
    if (highlight?.type === 'poll') {
      setActivePoll(highlight);
      return;
    }
    setWallLoading(true);
    setShowWallModal(highlight);
    try {
      let response;
      if (highlight.type === 'birthday') response = await axios.get(`${API}/walls/birthday/${highlight.user_id}`);
      else if (highlight.type === 'announcement') response = await axios.get(`${API}/announcements/${highlight.id}`);
      else if (highlight.type === 'event_memory') response = await axios.get(`${API}/event-memories/${highlight.id}`);
      setWallData(response?.data || null);
    } catch (e) {
      const gone = highlight.type === 'announcement' && [404, 410].includes(e.response?.status);
      setWallData(gone ? { unavailable: true } : null);
    }
    finally { setWallLoading(false); }
  };

  const sendWallMessage = async () => {
    if (!wallMessage.trim() || !showWallModal) return;
    try {
      let endpoint = '';
      if (showWallModal.type === 'birthday') endpoint = `${API}/walls/birthday/${showWallModal.user_id}/message`;
      else if (showWallModal.type === 'announcement') endpoint = `${API}/announcements/${showWallModal.id}/message`;
      if (endpoint) { await axios.post(endpoint, { content: wallMessage }); setWallMessage(''); openWall(showWallModal); }
    } catch { console.error('Failed to send message'); }
  };

  const reactToMessage = async (messageId, emoji) => {
    try { await axios.post(`${API}/walls/message/${messageId}/react?emoji=${encodeURIComponent(emoji)}`); if (showWallModal) openWall(showWallModal); }
    catch { console.error('Failed to react'); }
  };

  // === INTERACTION HANDLERS ===
  const handleLikePost = async (postId) => {
    const isDemoPost = postId.startsWith('demo_');
    if (isDemoPost) {
      setLikedPosts(prev => { const s = new Set(prev); s.has(postId) ? s.delete(postId) : s.add(postId); return s; });
      return;
    }
    // Optimistic update — flip the heart and adjust the count immediately so
    // the UI is responsive even on slow networks. We snapshot the prior state
    // so we can roll back if the API call fails.
    const wasLiked = likedPosts.has(postId);
    const prevPost = posts.find(p => p.id === postId);
    const prevCount = prevPost?.likes_count ?? 0;
    const optimisticCount = wasLiked ? Math.max(0, prevCount - 1) : prevCount + 1;
    setLikedPosts(prev => { const s = new Set(prev); wasLiked ? s.delete(postId) : s.add(postId); return s; });
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: optimisticCount } : p));
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`);
      // Reconcile with the server's authoritative count + liked state.
      setLikedPosts(prev => { const s = new Set(prev); response.data.liked ? s.add(postId) : s.delete(postId); return s; });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: response.data.total_likes ?? optimisticCount } : p));
    } catch {
      // Revert the optimistic flip on failure.
      setLikedPosts(prev => { const s = new Set(prev); wasLiked ? s.add(postId) : s.delete(postId); return s; });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: prevCount } : p));
      console.error('Failed to like post');
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    setActionLoading(prev => ({ ...prev, [`join_${challengeId}`]: true }));
    try {
      await axios.post(`${API}/challenges/${challengeId}/join`);
      setJoinedActions(prev => new Set([...prev, challengeId]));
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      if (error.response?.status === 400) setJoinedActions(prev => new Set([...prev, challengeId]));
    } finally { setActionLoading(prev => ({ ...prev, [`join_${challengeId}`]: false })); }
  };

  const handleShare = async (item, type) => {
    // Posts in the carousel are wrapped with `id: "post_<uuid>"` and the raw
    // UUID lives on `postId`. Always prefer the raw id so the shareable
    // `/posts/:postId` URL resolves on the detail page.
    const postUuid = item.postId || item.id;
    const challengeUuid = item.challengeId || item.id;
    const shareData = {
      title: type === 'post' ? 'Check out this post on SocialStar!' : item.title,
      text: type === 'post' ? item.content : item.description,
      url: window.location.origin + (type === 'post' ? `/posts/${postUuid}` : `/challenges/${challengeUuid}`)
    };
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`); alert('Link copied to clipboard!'); }
    } catch (error) {
      if (error.name !== 'AbortError') { try { await navigator.clipboard.writeText(shareData.url); alert('Link copied!'); } catch { alert('Unable to share'); } }
    }
  };

  // === COMMENT HANDLERS ===
  const handleOpenComments = async (itemId, itemType = 'post') => {
    setActivePostForComment(itemId); setActiveItemType(itemType); setShowCommentModal(true); setEditingComment(null); setEditCommentText('');
    if (itemType === 'post') { const post = posts.find(p => p.id === itemId); if (post?.comments) setPostComments(prev => ({ ...prev, [itemId]: post.comments })); }
    else if (itemType === 'mad_action') { if (!madActionComments[itemId]) setMadActionComments(prev => ({ ...prev, [itemId]: [] })); }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !activePostForComment) return;
    setActionLoading(prev => ({ ...prev, comment: true }));
    if (activeItemType === 'post') {
      try {
        const response = await axios.post(`${API}/posts/${activePostForComment}/comment`, { content: commentText, mentions: commentMentions });
        setPostComments(prev => ({ ...prev, [activePostForComment]: [...(prev[activePostForComment] || []), response.data] }));
        setPosts(prev => prev.map(p => p.id === activePostForComment ? { ...p, comments: [...(p.comments || []), response.data], comments_count: (p.comments_count ?? p.comments?.length ?? 0) + 1 } : p));
        setCommentText(''); setCommentMentions([]);
      } catch { console.error('Failed to add comment'); }
    } else {
      const newComment = { id: `comment_${Date.now()}`, content: commentText, mentions: commentMentions, user_name: user?.username || 'You', user_id: user?.id, created_at: new Date().toISOString() };
      setMadActionComments(prev => ({ ...prev, [activePostForComment]: [...(prev[activePostForComment] || []), newComment] }));
      setCommentText(''); setCommentMentions([]);
    }
    setActionLoading(prev => ({ ...prev, comment: false }));
  };

  const handleSaveEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    if (activeItemType === 'post') {
      try { await axios.put(`${API}/comments/${commentId}`, { content: editCommentText }); setPostComments(prev => ({ ...prev, [activePostForComment]: prev[activePostForComment].map(c => c.id === commentId ? { ...c, content: editCommentText } : c) })); }
      catch { console.error('Failed to edit comment'); }
    } else { setMadActionComments(prev => ({ ...prev, [activePostForComment]: prev[activePostForComment].map(c => c.id === commentId ? { ...c, content: editCommentText } : c) })); }
    setEditingComment(null); setEditCommentText('');
  };

  // === IDEA HANDLERS ===
  const handleSubmitIdea = async () => {
    if (ideaForm.title && ideaForm.description && selectedAdminEvent?.eventId) {
      try {
        await axios.post(`${API}/admin-events/${selectedAdminEvent.eventId}/suggest`, { title: ideaForm.title, description: ideaForm.description, agenda: ideaForm.agenda || null, location: ideaForm.location || null });
        setIdeaForm({ title: '', description: '', location: '', agenda: '' }); setShowSuggestIdeaModal(false); setRefreshKey(prev => prev + 1); alert('Idea submitted!');
      } catch (error) { alert(error.response?.data?.detail || 'Failed to submit idea'); }
    } else { alert('Please fill in all required fields'); }
  };

  // === FEED CARDS (memoized) ===
  const feedCards = useMemo(() => {
    const cards = [];
    // Admin events — only `suggestion`-type events still render as a
    // full card in the vertical feed. `voting`-type events have moved
    // into the Highlights strip as POLL tiles (see `pollHighlights`
    // below) and open the org poll modal on tap.
    adminEvents.filter(e => e.is_voting_open && e.event_type !== 'voting').forEach(event => {
      cards.push({ type: 'admin_event', id: `admin_event_${event.id}`, eventId: event.id, title: event.title, description: event.description, eventType: event.event_type, options: event.options || [], suggestions: event.suggestions || [], is_voting_open: event.is_voting_open, image_url: event.image_url, created_at: event.created_at || new Date().toISOString() });
    });
    // Real posts from DB
    posts.forEach(post => {
      const userName = post.user_name || post.username || 'Team Member';
      const names = userName.split(' ').filter(n => n.length > 0);
      const initials = names.length > 0 ? names.map(n => n[0] || '').join('').toUpperCase().slice(0, 2) : 'TM';
      const imgUrl = post.media_url && post.media_url.trim() ? post.media_url : null;
      cards.push({ type: 'user_post', id: `post_${post.id}`, user: { name: userName, initials }, timestamp: new Date(post.created_at).toLocaleDateString(), content: post.content, hashtags: post.tags?.map(t => `#${t}`) || [], image: imgUrl, postId: post.id, isProgress: post.is_mad_log || false, madChallengeId: post.mad_challenge_id || null, created_at: post.created_at, user_id: post.user_id, likesCount: post.likes_count ?? 0, commentsCount: post.comments_count ?? 0 });
    });
    // Order: admin cards first, then posts in the order the backend returned
    // them (already sorted created_at desc by GET /api/posts). No client-side
    // shuffle — a random shuffle on every `posts` state change caused the
    // whole feed to reorder each time a user liked a card.
    const adminCards = cards.filter(c => c.type === 'admin_event');
    const others = cards.filter(c => c.type !== 'admin_event');
    return [...adminCards, ...others];
  }, [posts, adminEvents]);

  // Poll tiles for the Highlights strip — one per open `voting`-type
  // admin_event. `star_reward` is optional (only rendered as a badge
  // when a positive number is attached to the event).
  const stripHighlights = useMemo(() => {
    const pollTiles = adminEvents
      .filter(e => e.event_type === 'voting')
      .map(e => ({
        id: `poll_${e.id}`,
        type: 'poll',
        eventId: e.id,
        title: e.title,
        options: e.options || [],
        image_url: e.image_url,
        star_reward: e.star_reward,
        my_vote: e.my_vote,
        is_closed: !!e.is_closed,
        winner_ids: e.winner_ids || [],
      }));
    return [...pollTiles, ...(highlights || [])];
  }, [adminEvents, highlights]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F5F7FA' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
          <Star size={40} fill="#52B8AC" color="#52B8AC" />
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F5F7FA', minHeight: '100vh', paddingBottom: '80px' }}>
      <TopHeader />
      <div style={{ width: '100%', maxWidth: 380, margin: '0 auto', padding: '0 16px' }}>

        {/* MAD repository — moved directly under the navbar */}
        <MADSection allChallenges={allChallenges} myChallenges={myChallenges}
          joinedActions={joinedActions} onJoinChallenge={handleJoinChallenge}
          currentUserId={user?.id} isHRAdmin={isHRAdmin} />

        {/* Highlights Strip — voting admin_events are surfaced here as
            POLL tiles (community-style card visuals). Tap opens
            `OrgPollModal`; existing announcement / birthday / memory
            tiles are unchanged. */}
        <HighlightsStrip highlights={stripHighlights} onOpenWall={openWall} />

        {/* User posts feed — vertical, full-width, dynamic height */}
        <div style={{ padding: '12px 0 16px' }}>
          <div data-testid="we-feed-carousel"
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {feedCards.filter(c => c.type !== 'terminal').map((card, cardIndex) => {
                const config = {
                  user_post: { accent: TEAL_MUTED, label: 'User Post', headerBg: '#FFFFFF', headerText: TEAL_ACCENT },
                  mad_action: { accent: ORANGE_MUTED, label: 'MAD Action', headerBg: '#FFFFFF', headerText: ORANGE_ACCENT },
                  admin_event: { accent: TEAL_ACCENT, label: 'Team Event', headerBg: TEAL_ACCENT, headerText: '#FFFFFF' }
                }[card.type] || { accent: TEAL_MUTED, label: 'Post', headerBg: '#FFFFFF', headerText: TEAL_ACCENT };

                return (
                  <div key={card.id} data-testid={`carousel-card-${cardIndex}`}
                    onClick={() => { if (card.type === 'mad_action' && card.challengeId) navigate(`/challenges/${card.challengeId}`); }}
                    style={{ width: '100%', cursor: card.type === 'mad_action' ? 'pointer' : 'default' }}>
                    <div style={{ background: '#FFFFFF', borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
                      {/* Header */}
                      <div style={{ background: config.headerBg, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                        {card.type === 'user_post' && card.isProgress && card.madChallengeId ? (
                          <button
                            type="button"
                            data-testid={`log-progress-label-${cardIndex}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/challenges/${card.madChallengeId}`); }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 10, fontWeight: 600, color: '#F28C28', textTransform: 'uppercase', letterSpacing: '0.5px', textDecoration: 'underline' }}
                          >
                            Log Progress
                          </button>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 600, color: config.headerText, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{config.label}</span>
                        )}
                        {card.type === 'admin_event' && <span style={{ fontSize: 11, fontWeight: 500, color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.4)', padding: '4px 10px', borderRadius: 6 }}>Interactive</span>}
                      </div>
                      {/* Content — dynamic height; no flex:1 or overflow:hidden so
                          text-only posts stay compact and image posts grow to fit. */}
                      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column' }}>
                        {/* User Post */}
                        {card.type === 'user_post' && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#F3F4F6', border: `2px solid ${TEAL_ACCENT}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEAL_ACCENT, fontWeight: 600, fontSize: 14 }}>
                                {card.user?.initials || 'U'}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{card.user?.name || 'Team Member'}</div>
                                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{formatDate(card.created_at)}</div>
                              </div>
                              {card.user_id === user?.id && (
                                <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: 'post', id: card.postId }); }}
                                  style={{ padding: 8, background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#9CA3AF' }}><Trash2 size={16} /></button>
                              )}
                              {isHRAdmin && card.user_id !== user?.id && (
                                <button onClick={(e) => { e.stopPropagation(); handleHRDeletePost(card.postId); }} data-testid="hr-delete-post"
                                  style={{ padding: '6px 10px', background: '#FEF2F2', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#DC2626', fontSize: 11, fontWeight: 500 }}>
                                  Remove
                                </button>
                              )}
                            </div>
                            {/* Image (16:9) — rendered ABOVE text when present */}
                            {card.image && (
                              <FeedPhoto src={card.image} alt={card.user?.name ? `Photo by ${card.user.name}` : 'Post photo'} testId={`feed-photo-${card.postId}`} style={{ borderRadius: 8, marginBottom: 12 }} />
                            )}
                            {/* Text — clamps to 3 lines, inline "Read more" toggle */}
                            <div style={{ marginBottom: 8 }}>
                              <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, margin: 0, display: expandedPosts.has(card.id) ? 'block' : '-webkit-box', WebkitLineClamp: expandedPosts.has(card.id) ? 'none' : 3, WebkitBoxOrient: 'vertical', overflow: expandedPosts.has(card.id) ? 'visible' : 'hidden' }}>{card.content}</p>
                              {card.content?.length > 150 && (
                                <button onClick={(e) => { e.stopPropagation(); setExpandedPosts(prev => { const s = new Set(prev); s.has(card.id) ? s.delete(card.id) : s.add(card.id); return s; }); }}
                                  style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, fontSize: 12, color: TEAL_ACCENT, fontWeight: 500, cursor: 'pointer' }}>
                                  {expandedPosts.has(card.id) ? 'Show less' : 'Read more'}
                                </button>
                              )}
                            </div>
                            {card.hashtags?.length > 0 && (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                                {card.hashtags.slice(0, 3).map((tag, i) => <span key={i} style={{ color: TEAL_ACCENT, fontSize: 11, fontWeight: 500 }}>{tag}</span>)}
                              </div>
                            )}
                          </>
                        )}
                        {/* Admin Event */}
                        {card.type === 'admin_event' && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                              <div style={{ width: 48, height: 48, borderRadius: 14, background: `linear-gradient(135deg, ${TEAL_ACCENT}15, ${TEAL_ACCENT}08)`, border: `2px solid ${TEAL_ACCENT}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Award size={24} color={TEAL_ACCENT} />
                              </div>
                              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', margin: 0, lineHeight: 1.3, flex: 1 }}>{card.title}</h3>
                            </div>
                            <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 16 }}>{card.eventType === 'voting' ? 'Vote now!' : card.description}</p>
                            {card.eventType === 'voting' && card.options && card.is_voting_open && (
                              <div style={{ marginBottom: 12 }}>
                                {card.options.map((option, optIdx) => {
                                  const totalVotes = card.options.reduce((s, o) => s + (o.votes?.length || 0), 0);
                                  const isSelected = option.votes?.includes(user?.id);
                                  const percentage = totalVotes > 0 ? Math.round((option.votes?.length || 0) / totalVotes * 100) : 0;
                                  const hasVoted = card.options.some(o => o.votes?.includes(user?.id));
                                  return (
                                    <button key={option.id || optIdx}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        // Optimistic vote: mutate adminEvents in place so the
                                        // tap registers instantly. Previously we did
                                        // await POST → setRefreshKey → refetch all 5
                                        // top-level page endpoints, adding ~2-3s of dead
                                        // time before the UI updated. Now the POST goes
                                        // to the server in the background; on failure we
                                        // roll back to the pre-tap snapshot.
                                        const originalEvents = adminEvents;
                                        setAdminEvents(prev => prev.map(ev => {
                                          if (ev.id !== card.eventId) return ev;
                                          return {
                                            ...ev,
                                            options: (ev.options || []).map(o => ({
                                              ...o,
                                              votes: o.id === option.id
                                                ? Array.from(new Set([...(o.votes || []), user.id]))
                                                : (o.votes || []).filter(uid => uid !== user.id),
                                            })),
                                          };
                                        }));
                                        try {
                                          await axios.post(`${API}/admin-events/${card.eventId}/vote/${option.id}`);
                                        } catch (err) {
                                          setAdminEvents(originalEvents);
                                          alert(err.response?.data?.detail || 'Failed to vote');
                                        }
                                      }}
                                      data-testid={`vote-option-${option.id}`}
                                      style={{ width: '100%', padding: '12px 14px', marginBottom: 8, background: isSelected ? '#D1FAE5' : '#F9FAFB', border: `2px solid ${isSelected ? TEAL_ACCENT : '#E5E7EB'}`, borderRadius: 10, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isSelected ? TEAL_ACCENT : '#D1D5DB'}`, background: isSelected ? TEAL_ACCENT : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {isSelected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'white' }} />}
                                      </div>
                                      <span style={{ flex: 1, fontSize: 14, fontWeight: isSelected ? 600 : 400, color: '#1F2937' }}>{option.text}</span>
                                      {hasVoted && <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>{percentage}%</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {card.eventType === 'suggestion' && card.is_voting_open && (
                              <>
                                {card.image_url && (
                                  <FeedPhoto src={card.image_url.startsWith('/api') ? `${API.replace('/api', '')}${card.image_url}` : card.image_url} alt={card.title}
                                    style={{ borderRadius: 12, marginBottom: 16 }} />
                                )}
                                <div style={{ display: 'flex', gap: 10 }}>
                                  <button onClick={(e) => { e.stopPropagation(); setSelectedAdminEvent(card); setShowSuggestIdeaModal(true); }}
                                    style={{ flex: 1, padding: '12px 14px', background: 'transparent', color: TEAL_ACCENT, border: `2px solid ${TEAL_ACCENT}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                    Suggest Idea
                                  </button>
                                  <button onClick={(e) => {
                                    e.stopPropagation(); setSelectedAdminEvent(card);
                                    const eventSuggestions = (card.suggestions || []).map((s, idx) => ({ id: s.id || `suggestion_${idx}`, title: s.title || 'Untitled', description: s.description || '', agenda: s.agenda || '', location: s.location || '', submittedBy: s.username || 'Unknown', userId: s.user_id, votes: s.votes?.length || 0, hasVoted: s.votes?.includes(user?.id) || false }));
                                    setSubmittedIdeas(eventSuggestions); setCurrentIdeaIndex(0); setShowVoteOnIdeasModal(true);
                                  }}
                                    style={{ flex: 1, padding: '12px 14px', background: 'transparent', color: ORANGE_ACCENT, border: `2px solid ${ORANGE_ACCENT}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                    Vote on Ideas
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      {/* Footer */}
                      <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(0,0,0,0.04)', background: '#FAFBFC', borderBottomLeftRadius: 20, borderBottomRightRadius: 20 }}>
                        {card.type === 'user_post' && (
                          <div style={{ display: 'flex', justifyContent: 'space-around' }} data-testid="user-post-actions">
                            <button data-testid="post-like-btn" onClick={(e) => { e.stopPropagation(); handleLikePost(card.postId); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: likedPosts.has(card.postId) ? '#EF4444' : '#9CA3AF', padding: '6px 12px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Heart size={22} fill={likedPosts.has(card.postId) ? '#EF4444' : 'none'} />
                              {/* Always show a numeric count — never blank. */}
                              <span data-testid="post-like-count" style={{ fontSize: 13, fontWeight: 500 }}>{card.likesCount ?? 0}</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleOpenComments(card.postId, 'post'); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: '8px 16px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <MessageCircle size={22} />
                              <span data-testid="post-comment-count" style={{ fontSize: 13, fontWeight: 500 }}>{card.commentsCount ?? 0}</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleShare(card, 'post'); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: '8px 16px', borderRadius: 8 }}>
                              <Share2 size={22} />
                            </button>
                          </div>
                        )}
                        {card.type === 'admin_event' && (() => {
                          // Total votes cast on THIS specific event card.
                          // For interactive/voting events, each `option.votes`
                          // is an array of user ids — sum lengths across
                          // options. For suggestion events, votes live on
                          // each suggestion under `s.votes`. Either way this
                          // is the real per-card number; the previous
                          // `submittedIdeas.length` was a modal-only counter
                          // that stayed 0 until the user opened Vote On Ideas.
                          const optionVotes = (card.options || []).reduce((s, o) => s + (o.votes?.length || 0), 0);
                          const suggestionVotes = (card.suggestions || []).reduce((s, sg) => s + (sg.votes?.length || 0), 0);
                          const totalVotes = optionVotes + suggestionVotes;
                          const label = totalVotes === 1 ? 'vote submitted' : 'votes submitted';
                          return (
                            <div
                              data-testid="admin-event-votes-count"
                              style={{ textAlign: 'center', fontSize: 12, color: '#6B7280', padding: '4px 0' }}
                            >
                              {totalVotes} {label}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Modals for Ideas */}
        <SuggestIdeaModal show={showSuggestIdeaModal} onClose={() => setShowSuggestIdeaModal(false)}
          ideaForm={ideaForm} setIdeaForm={setIdeaForm} onSubmit={handleSubmitIdea} />
        <VoteIdeasModal show={showVoteOnIdeasModal} onClose={() => { setShowVoteOnIdeasModal(false); setRefreshKey(p => p + 1); }}
          submittedIdeas={submittedIdeas} setSubmittedIdeas={setSubmittedIdeas}
          currentIdeaIndex={currentIdeaIndex} setCurrentIdeaIndex={setCurrentIdeaIndex}
          selectedAdminEvent={selectedAdminEvent} user={user} />

        {/* Poll Modal — opened from a POLL tile in the Highlights
            strip. Persists a vote via `POST /admin-events/{id}/vote/
            {option_id}` and optimistically mirrors the mutation into
            `adminEvents` so the feed / footer counts stay accurate
            without a refetch. */}
        {activePoll && (
          <OrgPollModal
            poll={activePoll}
            currentUserId={user?.id}
            onClose={() => setActivePoll(null)}
            onVoted={(eventId, optionId) => {
              setAdminEvents(prev => prev.map(ev => (ev.id === eventId ? { ...ev, my_vote: optionId } : ev)));
            }}
          />
        )}
      </div>

      {/* Comment Modal */}
      <CommentModal show={showCommentModal} activePostForComment={activePostForComment}
        activeItemType={activeItemType} commentText={commentText} setCommentText={setCommentText}
        commentMentions={commentMentions} setCommentMentions={setCommentMentions}
        postComments={postComments} madActionComments={madActionComments} posts={posts} user={user}
        editingComment={editingComment} editCommentText={editCommentText} setEditCommentText={setEditCommentText}
        onClose={() => { setShowCommentModal(false); setActivePostForComment(null); setEditingComment(null); }}
        onSubmit={handleSubmitComment}
        onEditComment={(c) => { setEditingComment(c.id); setEditCommentText(c.content); }}
        onSaveEditComment={handleSaveEditComment}
        onCancelEdit={() => { setEditingComment(null); setEditCommentText(''); }}
        onDeleteConfirm={setDeleteConfirm} actionLoading={actionLoading} />

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal deleteConfirm={deleteConfirm} onClose={() => setDeleteConfirm(null)}
        onDeletePost={handleDeletePost} onDeleteChallenge={handleDeleteChallenge} onDeleteComment={handleDeleteComment} />

      {/* Wall Modal */}
      <WallModal showWallModal={showWallModal} wallData={wallData} wallLoading={wallLoading}
        wallMessage={wallMessage} setWallMessage={setWallMessage}
        onClose={() => { setShowWallModal(null); setWallData(null); setWallMessage(''); }}
        onSendMessage={sendWallMessage} onReactToMessage={reactToMessage} user={user} />

      <BottomNav active="home" />
    </div>
  );
};

export default HomePage;
